import React, { useEffect, useState } from "react";
import { Box, MenuItem, Select, TextField, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import axios from "axios";
import PageLoader from "../components/Loader";
import { useToast } from "../components/Toast";
import {
  useEngagementTheme,
  engagementPageSx,
  EngagementPill,
  engagementButtonSx,
  engagementSelectSx,
} from "../components/engagementTheme";

const OPS_API = `${process.env.REACT_APP_API_HOST_URL}/ops/v1`;
const API = `${OPS_API}/engagement`;

const TERM_LABEL = { monthly: "Monthly", quarterly: "Quarterly", "bi-annual": "Bi-annual", annual: "Annual" };
const TERM_MONTHS = { monthly: 1, quarterly: 3, "bi-annual": 6, annual: 12 };

const METHOD_OPTIONS = [
  { value: "bank_transfer", label: "Bank transfer (NEFT/IMPS/RTGS)" },
  { value: "upi", label: "UPI to company account" },
  { value: "cheque", label: "Cheque or PDC" },
  { value: "cash", label: "Cash" },
];

const PREF_METHOD_OPTIONS = [
  "Bank transfer (NEFT/IMPS/RTGS)",
  "UPI to company account",
  "Razorpay link",
  "Cheque or PDC",
  "Cash",
];

const whenLabel = (card) => {
  if (card.stage === "renewed") return `Renewed ${new Date(card.subscriptionStart).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`;
  if (card.stage === "churned") return `Ended ${new Date(card.subscriptionEnd).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`;
  if (card.daysLeft < 0) return `Grace, ended ${Math.abs(card.daysLeft)} days ago`;
  return `Ends in ${card.daysLeft} day${card.daysLeft === 1 ? "" : "s"}`;
};

const formatAmount = (amount, currency) => (amount ? `${currency === "INR" ? "₹" : currency + " "}${Number(amount).toLocaleString("en-IN")}` : null);

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

const coversUntilPreview = (coversFrom, term) => {
  if (!coversFrom || !TERM_MONTHS[term]) return "—";
  const d = new Date(coversFrom);
  if (isNaN(d.getTime())) return "—";
  d.setMonth(d.getMonth() + TERM_MONTHS[term]);
  d.setDate(d.getDate() - 1);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

const defaultLinkDescription = (card) => {
  const months = TERM_MONTHS[card.term];
  const termText = months ? `${months} month${months > 1 ? "s" : ""}` : card.termLabel || "";
  return `scanO Engage ${card.plan || ""}, ${termText} renewal`.replace(/\s+/g, " ").trim();
};

const SendLinkPanel = ({ card, onSaved, onCancel }) => {
  const t = useEngagementTheme();
  const [amount, setAmount] = useState(card.amount || "");
  const [description, setDescription] = useState(defaultLinkDescription(card));
  const [expiryDays, setExpiryDays] = useState(7);
  const [saving, setSaving] = useState(false);
  const Toast = useToast();
  const auth = localStorage.getItem("token");

  const expiryNum = Number(expiryDays);
  const expiryValid = Number.isFinite(expiryNum) && expiryNum >= 1 && expiryNum <= 365;

  const send = async () => {
    if (!amount || Number(amount) <= 0) return;
    if (!expiryValid) {
      Toast.error("Enter a number of days between 1 and 365 for the link expiry.");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/renewals/${card.id}/payment-link`,
        { amount, description, expiryDays },
        { headers: { Authorization: `Bearer ${auth}` } }
      );
      onSaved(res.data.resent ? "Payment link resent." : "Payment link sent.", res.data.shortUrl);
    } catch (error) {
      Toast.error(error.response?.data?.message || "Could not send the payment link.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", padding: "16px", bgcolor: t.cardBg, border: `2px solid ${t.accent}`, borderRadius: "14px" }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "baseline", gap: "8px" }}>
        <Typography sx={{ fontSize: 18, fontWeight: 700 }}>Razorpay payment link: {card.doctorName}</Typography>
        <Typography sx={{ fontSize: 14, color: t.textMuted }}>{card.clinicName}{card.city ? `, ${card.city}` : ""}</Typography>
      </Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "16px", alignItems: "center" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Typography sx={{ fontSize: 13, color: t.textMuted }}>Amount on the payment page (₹)</Typography>
          <TextField size="small" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} sx={{ bgcolor: t.inputBg, width: 160 }} />
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          {card.amount != null && <Typography sx={{ fontSize: 13, color: t.textMuted }}>Plan price: {formatAmount(card.amount, card.currency)} ({card.plan}, {card.termLabel})</Typography>}
          {card.amount != null && Number(amount) === Number(card.amount) && (
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: t.tone.ok.color }}>Same as the plan price</Typography>
          )}
        </Box>
      </Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "16px", alignItems: "flex-end" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", flex: "1 1 320px" }}>
          <Typography sx={{ fontSize: 13, color: t.textMuted }}>Description the doctor sees</Typography>
          <TextField size="small" value={description} onChange={(e) => setDescription(e.target.value)} sx={{ bgcolor: t.inputBg }} />
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Typography sx={{ fontSize: 13, color: t.textMuted }}>Link expires in (days)</Typography>
          <TextField size="small" type="number" value={expiryDays} onChange={(e) => setExpiryDays(e.target.value)} error={!expiryValid} sx={{ bgcolor: t.inputBg, width: 120 }} />
          {!expiryValid && <Typography sx={{ fontSize: 12, color: t.tone.crit.color }}>1 to 365 days</Typography>}
        </Box>
      </Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "center" }}>
        <Box
          component="button"
          type="button"
          disabled={saving || !amount || Number(amount) <= 0 || !expiryValid}
          onClick={send}
          sx={{ ...engagementButtonSx(t, "solid"), opacity: saving || !amount || Number(amount) <= 0 || !expiryValid ? 0.6 : 1 }}
        >
          {saving ? "Sending…" : `Create and send link for ${formatAmount(amount, card.currency) || "—"}`}
        </Box>
        <Box component="button" type="button" onClick={onCancel} sx={{ ...engagementButtonSx(t), border: 0 }}>
          Cancel
        </Box>
        <Typography sx={{ fontSize: 13, color: t.textMuted }}>The payment records and verifies itself when the doctor pays.</Typography>
      </Box>
    </Box>
  );
};

// One shared panel for both entry points — the top "Record a payment" button
// (doctor searched from scratch) and a card's own "Record payment" action
// (doctor already known, field locked) — matching the prototype, which uses
// a single recordOpen panel for both.
const RecordPaymentPanel = ({ card, onSaved, onCancel }) => {
  const t = useEngagementTheme();
  const [query, setQuery] = useState(card ? `${card.doctorName}, ${card.clinicName}` : "");
  const [options, setOptions] = useState([]);
  const [doctor, setDoctor] = useState(card ? { _id: card.id, name: card.doctorName, clinicName: card.clinicName } : null);
  const [doctorPlan, setDoctorPlan] = useState(card?.plan || null);
  const [amount, setAmount] = useState(card?.amount || "");
  const [method, setMethod] = useState("cash");
  // Defaults to annual, not monthly — most real subscriptions are plain
  // yearly ones with no installmentType set on them at all, so card?.term
  // is almost always empty and shouldn't fall back to the rarer case.
  const [term, setTerm] = useState(card?.term || "annual");
  const [paidOn, setPaidOn] = useState(() => new Date().toISOString().slice(0, 10));
  const [coversFrom, setCoversFrom] = useState(() => new Date().toISOString().slice(0, 10));
  const [reference, setReference] = useState("");
  const [proof, setProof] = useState(null);
  const [saving, setSaving] = useState(false);
  const Toast = useToast();
  const auth = localStorage.getItem("token");

  const search = async (keyword) => {
    setQuery(keyword);
    setDoctor(null);
    if (!keyword || keyword.trim().length < 2) {
      setOptions([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/tickets/doctors-search`, {
        headers: { Authorization: `Bearer ${auth}` },
        params: { keyword },
      });
      setOptions(res.data.doctors || []);
    } catch {
      setOptions([]);
    }
  };

  const pickDoctor = async (o) => {
    setDoctor({ _id: o._id, name: o.name, clinicName: o.clinics?.map((c) => c.name).join(", ") || "" });
    setQuery(o.clinics?.length ? `${o.name}, ${o.clinics.map((c) => c.name).join(", ")}` : o.name);
    setOptions([]);
    setDoctorPlan(null);
    try {
      const res = await axios.get(`${API}/clinics/${o._id}`, { headers: { Authorization: `Bearer ${auth}` } });
      setDoctorPlan(res.data.data?.plan || null);
      const latestPrice = res.data.data?.subscriptionHistory?.[0]?.price;
      if (latestPrice != null) setAmount(latestPrice);
    } catch {
      setDoctorPlan(null);
    }
  };

  const save = async () => {
    if (!doctor || !amount || !coversFrom) return;
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("amount", amount);
      formData.append("method", method);
      formData.append("term", term);
      formData.append("paidOn", paidOn);
      formData.append("coversFrom", coversFrom);
      if (reference) formData.append("reference", reference);
      if (proof) formData.append("proof", proof);
      await axios.post(`${API}/renewals/${doctor._id}/payments`, formData, {
        headers: { Authorization: `Bearer ${auth}` },
      });
      onSaved("Saved for verification. It's in Reconciliation now.");
    } catch (error) {
      Toast.error(error.response?.data?.message || "Could not record this payment.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "14px", padding: "20px", bgcolor: t.cardBg, border: `2px solid ${t.accent}`, borderRadius: "14px" }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "baseline", gap: "8px" }}>
        <Typography sx={{ fontSize: 18, fontWeight: 700 }}>Record a payment</Typography>
        <Typography sx={{ fontSize: 13, color: t.textMuted }}>For bank transfer, UPI, cheque or cash. Razorpay payments record themselves.</Typography>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "flex-end" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", position: "relative" }}>
          <Typography sx={{ fontSize: 13, color: t.textMuted }}>Doctor and clinic</Typography>
          {card ? (
            <Box sx={{ ...engagementSelectSx(t), height: 44, display: "flex", alignItems: "center", padding: "0 10px", minWidth: 260, color: t.textMuted }}>{query}</Box>
          ) : (
            <>
              <TextField size="small" value={query} onChange={(e) => search(e.target.value)} placeholder="Search by doctor or clinic" sx={{ bgcolor: t.inputBg, width: 260 }} />
              {options.length > 0 && (
                <Box sx={{ position: "absolute", top: "100%", left: 0, zIndex: 5, width: 260, marginTop: "2px", display: "flex", flexDirection: "column", gap: "2px", bgcolor: t.cardBg, border: `1px solid ${t.border}`, borderRadius: "8px", padding: "4px" }}>
                  {options.map((o) => (
                    <Box key={o._id} onClick={() => pickDoctor(o)} sx={{ cursor: "pointer", padding: "6px 10px", borderRadius: "6px", bgcolor: t.hoverBg, fontSize: 14 }}>
                      {o.name}{o.clinics?.length ? ` — ${o.clinics.map((c) => c.name).join(", ")}` : ""}
                    </Box>
                  ))}
                </Box>
              )}
            </>
          )}
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Typography sx={{ fontSize: 13, color: t.textMuted }}>Plan paid for</Typography>
          <Box sx={{ ...engagementSelectSx(t), height: 44, display: "flex", alignItems: "center", padding: "0 10px", minWidth: 150, color: t.textMuted }}>
            {doctorPlan || "—"}
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Typography sx={{ fontSize: 13, color: t.textMuted }}>Method</Typography>
          <Select size="small" value={method} onChange={(e) => setMethod(e.target.value)} sx={{ ...engagementSelectSx(t), height: 44, minWidth: 190 }}>
            {METHOD_OPTIONS.map((m) => (
              <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
            ))}
          </Select>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Typography sx={{ fontSize: 13, color: t.textMuted }}>Term paid for</Typography>
          <Select size="small" value={term} onChange={(e) => setTerm(e.target.value)} sx={{ ...engagementSelectSx(t), height: 44, minWidth: 130 }}>
            {Object.entries(TERM_LABEL).map(([v, l]) => (
              <MenuItem key={v} value={v}>{l}</MenuItem>
            ))}
          </Select>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Typography sx={{ fontSize: 13, color: t.textMuted }}>Amount received (₹)</Typography>
          <TextField size="small" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} sx={{ bgcolor: t.inputBg, width: 150 }} />
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Typography sx={{ fontSize: 13, color: t.textMuted }}>Paid on</Typography>
          <TextField size="small" type="date" InputLabelProps={{ shrink: true }} value={paidOn} onChange={(e) => setPaidOn(e.target.value)} sx={{ bgcolor: t.inputBg }} />
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "flex-end" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Typography sx={{ fontSize: 13, color: t.textMuted }}>Covers from</Typography>
          <TextField size="small" type="date" InputLabelProps={{ shrink: true }} value={coversFrom} onChange={(e) => setCoversFrom(e.target.value)} sx={{ bgcolor: t.inputBg }} />
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", minHeight: 44, justifyContent: "center" }}>
          <Typography sx={{ fontSize: 13, color: t.textMuted }}>Covers until</Typography>
          <Typography sx={{ fontSize: 15, fontWeight: 700 }}>{coversUntilPreview(coversFrom, term)}</Typography>
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "flex-end" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", flex: "1 1 260px" }}>
          <Typography sx={{ fontSize: 13, color: t.textMuted }}>Reference: UTR, UPI ID or cheque number</Typography>
          <TextField size="small" value={reference} onChange={(e) => setReference(e.target.value)} sx={{ bgcolor: t.inputBg }} />
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Typography sx={{ fontSize: 13, color: t.textMuted }}>Proof (screenshot or PDF)</Typography>
          <Box component="input" type="file" accept="image/*,application/pdf" onChange={(e) => setProof(e.target.files?.[0] || null)} sx={{ font: "inherit", fontSize: 13, minHeight: 44 }} />
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "center" }}>
        <Box
          component="button"
          type="button"
          disabled={saving || !doctor || !amount || !coversFrom}
          onClick={save}
          sx={{ ...engagementButtonSx(t, "solid"), opacity: saving || !doctor || !amount || !coversFrom ? 0.6 : 1 }}
        >
          {saving ? "Saving…" : "Save for verification"}
        </Box>
        <Box component="button" type="button" onClick={onCancel} sx={{ ...engagementButtonSx(t), border: 0 }}>
          Cancel
        </Box>
        <Typography sx={{ fontSize: 13, color: t.textMuted }}>Accounts verifies it against the bank statement before the subscription is extended.</Typography>
      </Box>
    </Box>
  );
};

const REASON_OPTIONS = ["Needs more features", "Using fewer features", "Price", "Trying a shorter term first", "Other"];


// Labelled around what these actually change — the payment split, not the
// subscription's period length (that's the separate "New end date" field).
// No fixed surcharge % baked in here — it depends on the "Add extra charge?"
// toggle, so the detail text is built dynamically wherever it's shown.
const CHANGE_TERM_INFO = {
  annual: { label: "Full payment", count: 1 },
  monthly: { label: "Monthly installments", count: 12 },
  quarterly: { label: "Quarterly installments", count: 4 },
  "bi-annual": { label: "Bi-annual installments", count: 2 },
};
const CHANGE_TERM_OPTIONS = Object.keys(CHANGE_TERM_INFO);
const TAKES_EFFECT_OPTIONS = ["At renewal", "Now, prorated (upgrades only)"];

// Mirrors subscription.service.js's own INSTALLMENT_RULES/calculateInstallmentPricing
// exactly, so this preview always matches what the backend will actually charge.
const INSTALLMENT_RULES = {
  monthly: { count: 12, extraPercent: 15 },
  quarterly: { count: 4, extraPercent: 10 },
  "bi-annual": { count: 2, extraPercent: 5 },
};
const calculateInstallmentPricing = (basePrice, installmentType, waiveSurcharge = false) => {
  const rule = INSTALLMENT_RULES[installmentType];
  if (!rule) return null;
  const extraPercent = waiveSurcharge ? 0 : rule.extraPercent;
  const surcharge = (basePrice * extraPercent) / 100;
  return { baseAmount: basePrice, finalAmount: basePrice + surcharge, installmentCount: rule.count, extraPercent };
};

const ChangePlanPanel = ({ card, plans, onSaved, onCancel }) => {
  const t = useEngagementTheme();
  const [newPlanId, setNewPlanId] = useState("");
  const [newTerm, setNewTerm] = useState("annual");
  const [takesEffect, setTakesEffect] = useState(TAKES_EFFECT_OPTIONS[0]);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [reasonOption, setReasonOption] = useState(REASON_OPTIONS[0]);
  const [reasonOther, setReasonOther] = useState("");
  const [waiveSurcharge, setWaiveSurcharge] = useState(false);
  const [finalPriceOverride, setFinalPriceOverride] = useState("");
  const [endDateOverride, setEndDateOverride] = useState("");
  const [reference, setReference] = useState("");
  const [saving, setSaving] = useState(false);
  const Toast = useToast();
  const auth = localStorage.getItem("token");
  const authHeaders = { headers: { Authorization: `Bearer ${auth}` } };

  const currentPlan = plans.find((p) => String(p._id) === String(card.planId));
  const currentCatalogPrice = currentPlan?.geoPricing?.find((g) => g.geo === card.geo)?.price;

  const selectablePlans = plans.filter((p) => String(p._id) !== String(card.planId));
  const newPlan = plans.find((p) => String(p._id) === String(newPlanId));
  const newPlanCatalogPrice = newPlan?.geoPricing?.find((g) => g.geo === card.geo)?.price;
  const isDowngrade = currentCatalogPrice != null && newPlanCatalogPrice != null && Number(newPlanCatalogPrice) < Number(currentCatalogPrice);
  const gains = newPlan ? (newPlan.features || []).filter((f) => !(currentPlan?.features || []).includes(f)) : [];
  const loses = newPlan ? (currentPlan?.features || []).filter((f) => !(newPlan.features || []).includes(f)) : [];

  const reason = reasonOption === "Other" ? reasonOther.trim() : reasonOption;
  const isNow = !isDowngrade && takesEffect === TAKES_EFFECT_OPTIONS[1]; // "Now, prorated (upgrades only)"
  const loadPreview = async (planId, effect, downgrade) => {
    if (!planId || downgrade || effect !== TAKES_EFFECT_OPTIONS[1]) {
      setPreview(null);
      return;
    }
    setLoadingPreview(true);
    try {
      const res = await axios.post(`${OPS_API}/preivew-change-plan`, { subscriptionId: card.subscriptionId, newPlanId: planId }, authHeaders);
      setPreview(res.data.data);
    } catch (error) {
      Toast.error(error.response?.data?.message || "Could not load the plan change preview.");
    } finally {
      setLoadingPreview(false);
    }
  };

  // A downgrade can only ever land on the next renewal — same reasoning the
  // real modal uses to force "later" and hide the timing choice entirely.
  const pickPlan = (planId) => {
    setNewPlanId(planId);
    const plan = plans.find((p) => String(p._id) === String(planId));
    const planPrice = plan?.geoPricing?.find((g) => g.geo === card.geo)?.price;
    const downgrade = currentCatalogPrice != null && planPrice != null && Number(planPrice) < Number(currentCatalogPrice);
    const effect = downgrade ? TAKES_EFFECT_OPTIONS[0] : takesEffect;
    if (downgrade) setTakesEffect(TAKES_EFFECT_OPTIONS[0]);
    loadPreview(planId, effect, downgrade);
  };

  const pickTakesEffect = (value) => {
    setTakesEffect(value);
    loadPreview(newPlanId, value, isDowngrade);
  };

  // "Now" reuses the same changePlan the Doctor tab's Upgrade Plan button
  // uses. "At renewal" reuses renewSubscription with a newPlanId and no
  // inPlace flag — the exact same path the Doctor tab's own Renew modal
  // takes when "When should this apply? Later" is picked, which creates the
  // next chained subscription period already on the new plan. "New term",
  // the surcharge waiver, the final-price override and the end-date override
  // all only apply to this path — changePlan (the "Now" path) doesn't take
  // any of them; it's always a plain prorated top-up at catalog price.
  const installmentBasePrice = (finalPriceOverride !== "" && Number(finalPriceOverride) > 0) ? Number(finalPriceOverride) : newPlanCatalogPrice;
  const installmentPreview = newTerm !== "annual" && installmentBasePrice != null
    ? calculateInstallmentPricing(Number(installmentBasePrice), newTerm, waiveSurcharge)
    : null;

  const confirm = async () => {
    if (!newPlanId || !reason) return;
    setSaving(true);
    try {
      if (isNow) {
        if (!preview) return;
        await axios.post(
          `${OPS_API}/change-plan`,
          { subscriptionId: card.subscriptionId, newPlanId, reason, paymentData: { metadata: { source: "engagement-ops" }, reference: reference.trim() || undefined } },
          authHeaders
        );
        onSaved("Plan changed.");
      } else {
        const payment = newTerm === "annual"
          ? { isInstallment: false, metadata: { source: "engagement-ops" }, reference: reference.trim() || undefined }
          : { isInstallment: true, installmentType: newTerm, waiveSurcharge, metadata: { source: "engagement-ops" }, reference: reference.trim() || undefined };
        const body = { subscriptionId: card.subscriptionId, startDate: card.subscriptionEnd, clinics: [], newPlanId, reason, payment };
        if (finalPriceOverride !== "" && Number(finalPriceOverride) > 0) body.customPrice = Number(finalPriceOverride);
        if (endDateOverride) body.endDate = endDateOverride;
        await axios.post(`${OPS_API}/subscription/renew`, body, authHeaders);
        onSaved("Plan change scheduled for the next renewal.");
      }
    } catch (error) {
      Toast.error(error.response?.data?.message || "Could not change this plan.");
    } finally {
      setSaving(false);
    }
  };

  const newPlanPrice = newPlan?.geoPricing?.find((g) => g.geo === card.geo);
  const currency = newPlanPrice?.currency || "₹";
  const cycleWord = (isNow ? preview?.billingCycle : newPlan?.billingCycle) === "monthly" ? "month" : "year";
  const priceDiff = isNow && preview && !preview.samePlan ? Number(preview.NEW_FULL_PRICE) - Number(preview.OLD_FULL_PRICE) : null;
  const fullPriceForDisplay = isNow ? preview?.NEW_FULL_PRICE : newPlanPrice?.price;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "14px", padding: "20px", bgcolor: t.cardBg, border: `2px solid ${t.accent}`, borderRadius: "14px" }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "baseline", gap: "8px" }}>
        <Typography sx={{ fontSize: 18, fontWeight: 700 }}>Change plan: {card.doctorName}</Typography>
        <Typography sx={{ fontSize: 14, color: t.textMuted }}>Now on {card.plan}, {card.termLabel}, {card.amountText}</Typography>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "16px", alignItems: "flex-end" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Typography sx={{ fontSize: 13, color: t.textMuted }}>New plan</Typography>
          <Select size="small" value={newPlanId} onChange={(e) => pickPlan(e.target.value)} displayEmpty sx={{ ...engagementSelectSx(t), height: 44, minWidth: 190 }}>
            <MenuItem value="">Select a new plan</MenuItem>
            {selectablePlans.map((p) => {
              const regionPrice = p.geoPricing?.find((g) => g.geo === card.geo);
              return (
                <MenuItem key={p._id} value={p._id}>
                  {p.name}{regionPrice ? ` — ${regionPrice.currency} ${regionPrice.price}` : ""}
                </MenuItem>
              );
            })}
            {selectablePlans.length === 0 && <MenuItem value="" disabled>No other plan available</MenuItem>}
          </Select>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Typography sx={{ fontSize: 13, color: t.textMuted }}>Reference: UTR, UPI ID or cheque number</Typography>
          <TextField size="small" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Optional" sx={{ bgcolor: t.inputBg, width: 220 }} />
        </Box>
        {!isNow && (
          <>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <Typography sx={{ fontSize: 13, color: t.textMuted }}>Payment schedule</Typography>
              <Select size="small" value={newTerm} onChange={(e) => setNewTerm(e.target.value)} sx={{ ...engagementSelectSx(t), height: 44, minWidth: 220 }}>
                {CHANGE_TERM_OPTIONS.map((o) => {
                  const rule = INSTALLMENT_RULES[o];
                  const detail = o === "annual" ? "One payment, no installments" : `${CHANGE_TERM_INFO[o].count} payments${waiveSurcharge ? ", no surcharge" : `, +${rule.extraPercent}% surcharge`}`;
                  return <MenuItem key={o} value={o}>{CHANGE_TERM_INFO[o].label} — {detail}</MenuItem>;
                })}
              </Select>
            </Box>
            {newTerm !== "annual" && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                <Typography sx={{ fontSize: 13, color: t.textMuted }}>Add extra charge?</Typography>
                <Select size="small" value={waiveSurcharge ? "true" : "false"} onChange={(e) => setWaiveSurcharge(e.target.value === "true")} sx={{ ...engagementSelectSx(t), height: 44, minWidth: 190 }}>
                  <MenuItem value="false">Yes, add extra charge</MenuItem>
                  <MenuItem value="true">No extra charge</MenuItem>
                </Select>
              </Box>
            )}
            <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <Typography sx={{ fontSize: 13, color: t.textMuted }}>Final price (optional override)</Typography>
              <TextField
                size="small"
                type="number"
                placeholder={newPlanCatalogPrice != null ? String(newPlanCatalogPrice) : "Catalog price"}
                value={finalPriceOverride}
                onChange={(e) => setFinalPriceOverride(e.target.value)}
                inputProps={{ min: 0 }}
                sx={{ bgcolor: t.inputBg, width: 190 }}
              />
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <Typography sx={{ fontSize: 13, color: t.textMuted }}>New end date (optional override)</Typography>
              <TextField
                size="small"
                type="date"
                value={endDateOverride}
                onChange={(e) => setEndDateOverride(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ bgcolor: t.inputBg, width: 190 }}
              />
            </Box>
          </>
        )}
        {newPlanId && !isDowngrade && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <Typography sx={{ fontSize: 13, color: t.textMuted }}>Takes effect</Typography>
            <Select size="small" value={takesEffect} onChange={(e) => pickTakesEffect(e.target.value)} sx={{ ...engagementSelectSx(t), height: 44, minWidth: 220 }}>
              {TAKES_EFFECT_OPTIONS.map((o) => (
                <MenuItem key={o} value={o}>{o}</MenuItem>
              ))}
            </Select>
          </Box>
        )}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Typography sx={{ fontSize: 13, color: t.textMuted }}>Reason</Typography>
          <Select size="small" value={reasonOption} onChange={(e) => setReasonOption(e.target.value)} sx={{ ...engagementSelectSx(t), height: 44, minWidth: 180 }}>
            {REASON_OPTIONS.map((r) => (
              <MenuItem key={r} value={r}>{r}</MenuItem>
            ))}
          </Select>
        </Box>
        {reasonOption === "Other" && (
          <TextField size="small" label="Reason" value={reasonOther} onChange={(e) => setReasonOther(e.target.value)} sx={{ bgcolor: t.inputBg, flex: "1 1 220px" }} />
        )}
      </Box>

      {isNow && loadingPreview && <Typography sx={{ fontSize: 13, color: t.textMuted }}>Loading preview…</Typography>}

      {isNow && preview?.samePlan && <Typography sx={{ fontSize: 13, color: t.tone.crit.color }}>Already on this plan — pick a different one.</Typography>}

      {newPlanId && (!isNow || (preview && !loadingPreview && !preview.samePlan)) && (
        <>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "14px" }}>
            <Box sx={{ flex: "1 1 220px", padding: "14px", borderRadius: "10px", bgcolor: t.hoverBg, display: "flex", flexDirection: "column", gap: "4px" }}>
              <Typography sx={{ fontSize: 13, color: t.textMuted }}>Change</Typography>
              <EngagementPill t={t} tone={isDowngrade ? "warn" : "ok"} sx={{ alignSelf: "flex-start" }}>{isDowngrade ? "Downgrade" : "Upgrade"}</EngagementPill>
              <Typography sx={{ fontSize: 15, fontWeight: 700 }}>{currency} {fullPriceForDisplay} for a {cycleWord}</Typography>
              {isNow && priceDiff != null && (
                <Typography sx={{ fontSize: 13, color: t.textSecondary }}>{priceDiff > 0 ? "Up" : priceDiff < 0 ? "Down" : "Same price,"} {currency} {Math.abs(priceDiff)} a {cycleWord}</Typography>
              )}
              {!isNow && (
                <Typography sx={{ fontSize: 13, color: t.textSecondary }}>
                  Starts {formatDate(card.subscriptionEnd)}, full price, no proration
                </Typography>
              )}
            </Box>
            <Box sx={{ flex: "1 1 220px", padding: "14px", borderRadius: "10px", bgcolor: t.tone.ok.bg, display: "flex", flexDirection: "column", gap: "4px" }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: t.tone.ok.color }}>Gains</Typography>
              <Typography sx={{ fontSize: 14 }}>{gains.length ? gains.join(", ") : "Nothing new"}</Typography>
            </Box>
            <Box sx={{ flex: "1 1 220px", padding: "14px", borderRadius: "10px", bgcolor: t.tone.crit.bg, display: "flex", flexDirection: "column", gap: "4px" }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: t.tone.crit.color }}>Loses</Typography>
              <Typography sx={{ fontSize: 14 }}>{loses.length ? loses.join(", ") : "Nothing"}</Typography>
            </Box>
          </Box>

          {isDowngrade && (
            <Typography sx={{ margin: 0, padding: "10px 14px", borderRadius: "8px", bgcolor: t.tone.warn.bg, color: t.tone.warn.color, fontSize: 14, fontWeight: 600 }}>
              Downgrade: data from the features they lose stays in the account but becomes read-only. Offer a training session or a longer term before confirming; the lead sees every downgrade.
            </Typography>
          )}

          {!isNow && installmentPreview && (
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px", padding: "14px", bgcolor: t.hoverBg, borderRadius: "10px", fontSize: 13 }}>
              <span>Base amount: <strong>{currency} {installmentPreview.baseAmount}</strong></span>
              <span>Extra charge: <strong>{installmentPreview.extraPercent}%</strong></span>
              <span>Total amount: <strong>{currency} {installmentPreview.finalAmount}</strong></span>
              <span>Installments: <strong>{installmentPreview.installmentCount}</strong></span>
            </Box>
          )}

          {isNow && preview && !preview.samePlan && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", padding: "10px", bgcolor: t.hoverBg, borderRadius: "8px" }}>
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px", fontSize: 13 }}>
                <span>Days used: <strong>{preview.daysUsed}</strong></span>
                <span>Days remaining: <strong>{preview.daysRemaining}</strong></span>
                <span>Remaining old value: <strong>{currency} {preview.remainingOldValue}</strong></span>
                <span>New remaining value: <strong>{currency} {preview.newRemainingValue}</strong></span>
              </Box>
              <Typography sx={{ fontSize: 13, color: t.textMuted }}>Amount to collect now</Typography>
              <Typography sx={{ fontSize: 22, fontWeight: 700, color: preview.collectFinal > 0 ? t.tone.crit.color : t.tone.ok.color }}>
                {currency} {preview.collectFinal}
              </Typography>
              {preview.refund > 0 && <EngagementPill t={t} tone="ok" sx={{ alignSelf: "flex-start" }}>Refund to doctor: {currency} {preview.refund}</EngagementPill>}
            </Box>
          )}
        </>
      )}

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "center" }}>
        <Box
          component="button"
          type="button"
          disabled={saving || !newPlanId || !reason || (isNow && !preview)}
          onClick={confirm}
          sx={{ ...engagementButtonSx(t, "solid"), opacity: saving || !newPlanId || !reason || (isNow && !preview) ? 0.6 : 1 }}
        >
          {saving ? "Saving…" : "Confirm plan change"}
        </Box>
        <Box component="button" type="button" onClick={onCancel} sx={{ ...engagementButtonSx(t), border: 0 }}>
          Cancel
        </Box>
        <Typography sx={{ fontSize: 13, color: t.textMuted }}>
          {isNow
            ? "Logged on the clinic timeline. The new plan applies immediately, prorated for the rest of this cycle."
            : "Logged on the clinic timeline. The new plan switches on at the next renewal, full price, no proration."}
        </Typography>
      </Box>
    </Box>
  );
};

const EngagementRenewals = () => {
  const t = useEngagementTheme();
  const [view, setView] = useState("Board");
  const [board, setBoard] = useState({ tiles: {}, columns: [] });
  const [officers, setOfficers] = useState([]);
  const [owner, setOwner] = useState("all");
  const [term, setTerm] = useState("all");
  const [method, setMethod] = useState("any");
  const [boardPage, setBoardPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [sendingId, setSendingId] = useState("");
  const [sendLinkId, setSendLinkId] = useState("");
  const [recordingId, setRecordingId] = useState("");
  const [changingId, setChangingId] = useState("");
  const [churningId, setChurningId] = useState("");
  const [churnPromptId, setChurnPromptId] = useState("");
  const [churnReason, setChurnReason] = useState("");
  const [pausingId, setPausingId] = useState("");
  const [notesOpenId, setNotesOpenId] = useState("");
  const [notesDraft, setNotesDraft] = useState("");
  const [savingNotesId, setSavingNotesId] = useState("");
  const [plans, setPlans] = useState([]);
  const [reconciliation, setReconciliation] = useState([]);
  const [reconciliationPage, setReconciliationPage] = useState(1);
  const [reconciliationTotalPages, setReconciliationTotalPages] = useState(1);
  const [reconciliationTotalCount, setReconciliationTotalCount] = useState(0);
  const [history, setHistory] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [verifyingId, setVerifyingId] = useState("");
  const [rejectingId, setRejectingId] = useState("");
  const [collectingId, setCollectingId] = useState("");
  const [uploadingInvoiceId, setUploadingInvoiceId] = useState("");
  const [historySearch, setHistorySearch] = useState("");
  const [toast, setToast] = useState("");
  const Toast = useToast();
  const auth = localStorage.getItem("token");
  const authHeaders = { headers: { Authorization: `Bearer ${auth}` } };

  const sendLink = async (doctorId) => {
    setSendingId(doctorId);
    try {
      const res = await axios.post(`${API}/renewals/${doctorId}/payment-link`, {}, authHeaders);
      setToast(res.data.resent ? "Payment link resent." : "Payment link sent.");
      setBoard((prev) => ({
        ...prev,
        columns: prev.columns.map((col) => ({
          ...col,
          cards: col.cards.map((c) =>
            c.id === doctorId ? { ...c, paymentLinkSent: true, paymentLinkUrl: res.data.shortUrl } : c
          ),
        })),
      }));
    } catch (error) {
      Toast.error(error.response?.data?.message || "Could not send the payment link.");
    } finally {
      setSendingId("");
    }
  };

  const markChurned = async (doctorId, reason) => {
    setChurningId(doctorId);
    try {
      await axios.patch(`${API}/renewals/${doctorId}/mark-churned`, { reason }, authHeaders);
      setToast("Marked churned.");
      setChurnPromptId("");
      setChurnReason("");
      loadBoard();
    } catch (error) {
      Toast.error(error.response?.data?.message || "Could not mark this clinic churned.");
    } finally {
      setChurningId("");
    }
  };

  // Pauses/resumes the real subscription (status: 'active' <-> 'paused') —
  // the same core pauseSubscription/unpauseSubscription the Doctor tab uses,
  // not a board-only cosmetic flag like "Mark churned".
  const pauseOrResume = async (card, resume) => {
    setPausingId(card.id);
    try {
      await axios.post(
        `${OPS_API}/subscription/${resume ? "unpause" : "pause"}`,
        { subscriptionId: card.subscriptionId, reason: resume ? "Resumed by Engagement" : "Paused by Engagement" },
        authHeaders
      );
      setToast(resume ? "Subscription resumed." : "Subscription paused.");
      loadBoard();
    } catch (error) {
      Toast.error(error.response?.data?.message || "Could not update this subscription.");
    } finally {
      setPausingId("");
    }
  };

  const toggleNotes = (c) => {
    setNotesOpenId(notesOpenId === c.id ? "" : c.id);
    setNotesDraft("");
  };

  const addNote = async (doctorId) => {
    if (!notesDraft.trim()) return;
    setSavingNotesId(doctorId);
    try {
      const res = await axios.post(`${API}/clinics/${doctorId}/notes`, { text: notesDraft }, authHeaders);
      setToast("Note added.");
      setBoard((prev) => ({
        ...prev,
        columns: prev.columns.map((col) => ({
          ...col,
          cards: col.cards.map((c) => (c.id === doctorId ? { ...c, notes: res.data.data } : c)),
        })),
      }));
      setNotesDraft("");
    } catch (error) {
      Toast.error(error.response?.data?.message || "Could not add this note.");
    } finally {
      setSavingNotesId("");
    }
  };

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/officers`, authHeaders)
      .then((res) => {
        if (!cancelled) setOfficers(res.data.officers || []);
      })
      .catch(() => {});
    axios
      .get(`${process.env.REACT_APP_API_HOST_URL}/v1/subscription/plan`, authHeaders)
      .then((res) => {
        if (!cancelled) setPlans(res.data.plans || []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadBoard = (page = boardPage) => {
    setLoading(true);
    axios
      .get(`${API}/renewals`, { ...authHeaders, params: { owner, term, method, page, limit: 15 } })
      .then((res) => setBoard(res.data))
      .catch(() => Toast.error("Error while fetching renewals"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    setBoardPage(1);
    loadBoard(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [owner, term, method]);

  useEffect(() => {
    loadBoard(boardPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardPage]);

  const loadReconciliation = (page = reconciliationPage) => {
    setLoading(true);
    axios
      .get(`${API}/renewals/reconciliation`, { ...authHeaders, params: { owner, page, limit: 20 } })
      .then((res) => {
        setReconciliation(res.data.data || []);
        setReconciliationTotalPages(res.data.totalPages || 1);
        setReconciliationTotalCount(res.data.totalCount || 0);
      })
      .catch(() => Toast.error("Error while fetching reconciliation"))
      .finally(() => setLoading(false));
  };

  const loadHistory = (page = historyPage) => {
    setLoading(true);
    axios
      .get(`${API}/renewals/payments`, { ...authHeaders, params: { owner, q: historySearch || undefined, page, limit: 20 } })
      .then((res) => {
        setHistory(res.data.data || []);
        setHistoryTotalPages(res.data.totalPages || 1);
      })
      .catch(() => Toast.error("Error while fetching payment history"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (view === "Reconciliation") loadReconciliation(1);
    if (view === "Payment history") loadHistory(1);
    setReconciliationPage(1);
    setHistoryPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, owner]);

  useEffect(() => {
    if (view === "Reconciliation") loadReconciliation(reconciliationPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reconciliationPage]);

  useEffect(() => {
    if (view !== "Payment history") return;
    const timeout = setTimeout(() => loadHistory(historyPage), historySearch ? 400 : 0);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historyPage, historySearch]);

  const verify = async (paymentId) => {
    setVerifyingId(paymentId);
    try {
      await axios.patch(`${API}/renewals/payments/${paymentId}/verify`, {}, authHeaders);
      setToast("Payment verified.");
      loadReconciliation();
    } catch (error) {
      Toast.error(error.response?.data?.message || "Could not verify this payment.");
    } finally {
      setVerifyingId("");
    }
  };

  const reject = async (paymentId) => {
    setRejectingId(paymentId);
    try {
      await axios.patch(`${API}/renewals/payments/${paymentId}/reject`, {}, authHeaders);
      setToast("Payment rejected. It moves back to grace.");
      loadReconciliation();
    } catch (error) {
      Toast.error(error.response?.data?.message || "Could not reject this payment.");
    } finally {
      setRejectingId("");
    }
  };

  const collectInstallment = async (paymentId) => {
    setCollectingId(paymentId);
    try {
      await axios.post(`${OPS_API}/subscription/pay-installment`, { paymentId }, authHeaders);
      setToast("Installment collected.");
      loadHistory();
    } catch (error) {
      Toast.error(error.response?.data?.message || "Could not collect this installment.");
    } finally {
      setCollectingId("");
    }
  };

  const uploadInvoice = async (paymentId, file) => {
    if (!file) return;
    setUploadingInvoiceId(paymentId);
    try {
      const formData = new FormData();
      formData.append("invoice", file);
      const res = await axios.post(`${API}/renewals/payments/${paymentId}/invoice`, formData, authHeaders);
      setToast("Invoice uploaded.");
      setHistory((prev) => prev.map((p) => (p.id === paymentId ? res.data.data : p)));
    } catch (error) {
      Toast.error(error.response?.data?.message || "Could not upload this invoice.");
    } finally {
      setUploadingInvoiceId("");
    }
  };

  const activeCard = board.columns
    ?.flatMap((col) => col.cards)
    .find((c) => c.id === sendLinkId || c.id === changingId || c.id === recordingId);

  const closeAllPanels = () => {
    setSendLinkId("");
    setRecordingId("");
    setChangingId("");
  };

  const toggleRecordGeneral = () => {
    if (recordingId === "general") {
      setRecordingId("");
    } else {
      closeAllPanels();
      setRecordingId("general");
    }
  };

  const toggleSendLink = (id) => {
    if (sendLinkId === id) {
      setSendLinkId("");
    } else {
      closeAllPanels();
      setSendLinkId(id);
    }
  };

  const toggleRecording = (id) => {
    if (recordingId === id) {
      setRecordingId("");
    } else {
      closeAllPanels();
      setRecordingId(id);
    }
  };

  const toggleChanging = (id) => {
    if (changingId === id) {
      setChangingId("");
    } else {
      closeAllPanels();
      setChangingId(id);
    }
  };

  const tiles = [
    { label: "In window or due this week", value: board.tiles.endingIn90 || 0, note: "Across " + (owner === "all" ? "everyone" : "this officer") },
    { label: "In grace period", value: board.tiles.inGrace || 0, note: `Unpaid, within ${board.graceDays || 15} days of the end date` },
    { label: "Payments to verify", value: board.tiles.verifying || 0, note: "Bank transfer, UPI, cheque or cash" },
    { label: "On monthly plans", value: board.tiles.onMonthly || 0, note: "Due every month" },
    { label: "Renewed this month", value: board.tiles.renewedThisMonth || 0, note: "A new subscription started this month" },
    { label: "Churned", value: board.tiles.churned || 0, note: "Past grace, no renewal" },
  ];

  return (
    <Box sx={engagementPageSx(t)}>
      <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "flex-end", gap: "16px" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Typography sx={{ fontSize: 30, fontWeight: 700, letterSpacing: "-0.015em" }}>Renewals</Typography>
          <Typography sx={{ fontSize: 15, color: t.textMuted }}>
            Every clinic renews on its own term. The renewal window opens 90 days before the end for bi-annual plans,
            45 days for quarterly, and 7 days for monthly. Grace lasts {board.graceDays || 15} days after the end date.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: "10px" }}>
          <Box component="button" type="button" onClick={toggleRecordGeneral} sx={engagementButtonSx(t, "solid")}>
            {recordingId === "general" ? "Close" : "Record a payment"}
          </Box>
        </Box>
      </Box>

      {toast && (
        <Box role="status" sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: "12px", padding: "14px 18px", borderRadius: "12px", bgcolor: t.tone.ok.bg, color: t.tone.ok.color, fontSize: 15, fontWeight: 600 }}>
          <span>{toast}</span>
          <Box component="button" type="button" onClick={() => setToast("")} sx={{ minHeight: 44, padding: "0 14px", border: `1px solid ${t.tone.ok.color}`, borderRadius: "8px", bgcolor: "transparent", color: t.tone.ok.color, font: "inherit", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
            Dismiss
          </Box>
        </Box>
      )}

      {sendLinkId && activeCard && (
        <SendLinkPanel
          card={activeCard}
          onSaved={(message) => { setSendLinkId(""); setToast(message); loadBoard(); }}
          onCancel={() => setSendLinkId("")}
        />
      )}

      {recordingId && (
        <RecordPaymentPanel
          card={recordingId === "general" ? null : activeCard}
          onSaved={(message) => { setRecordingId(""); setToast(message); loadBoard(); }}
          onCancel={() => setRecordingId("")}
        />
      )}

      {changingId && activeCard && (
        <ChangePlanPanel
          card={activeCard}
          plans={plans}
          onSaved={(message) => { setChangingId(""); setToast(message); loadBoard(); }}
          onCancel={() => setChangingId("")}
        />
      )}

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: "14px" }}>
        {tiles.map((tile) => (
          <Box key={tile.label} sx={{ display: "flex", flexDirection: "column", gap: "4px", padding: "16px 18px", bgcolor: t.cardBg, border: `1px solid ${t.border}`, borderRadius: "14px" }}>
            <Typography sx={{ fontSize: 14, color: t.textMuted }}>{tile.label}</Typography>
            <Typography sx={{ fontSize: 26, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{tile.value}</Typography>
            <Typography sx={{ fontSize: 13, color: t.textSecondary }}>{tile.note}</Typography>
          </Box>
        ))}
      </Box>

      <Box role="tablist" sx={{ display: "flex", gap: "4px", flexWrap: "wrap", borderBottom: `1px solid ${t.border}` }}>
        {["Board", "Reconciliation", "Payment history"].map((v) => (
          <Box
            key={v}
            component="button"
            type="button"
            onClick={() => setView(v)}
            sx={{
              minHeight: 48, padding: "0 14px", border: 0, borderBottom: "3px solid", borderBottomColor: view === v ? t.accent : "transparent",
              bgcolor: "transparent", font: "inherit", fontSize: 15, fontWeight: 600, cursor: "pointer", color: view === v ? t.textPrimary : t.textMuted,
            }}
          >
            {v}{v === "Reconciliation" && reconciliationTotalCount > 0 ? ` (${reconciliationTotalCount})` : ""}
          </Box>
        ))}
      </Box>

      {view === "Board" && (
        <>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "12px", alignItems: "flex-end" }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <Typography sx={{ fontSize: 13, color: t.textMuted }}>Term</Typography>
              <Select size="small" value={term} onChange={(e) => setTerm(e.target.value)} sx={{ ...engagementSelectSx(t), minWidth: 140 }}>
                <MenuItem value="all">All terms</MenuItem>
                {Object.entries(TERM_LABEL).map(([v, l]) => (
                  <MenuItem key={v} value={v}>{l}</MenuItem>
                ))}
              </Select>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <Typography sx={{ fontSize: 13, color: t.textMuted }}>Prefers to pay by</Typography>
              <Select size="small" value={method} onChange={(e) => setMethod(e.target.value)} sx={{ ...engagementSelectSx(t), minWidth: 190 }}>
                <MenuItem value="any">Any method</MenuItem>
                {PREF_METHOD_OPTIONS.map((m) => (
                  <MenuItem key={m} value={m}>{m}</MenuItem>
                ))}
              </Select>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <Typography sx={{ fontSize: 13, color: t.textMuted }}>Owner</Typography>
              <Select size="small" value={owner} onChange={(e) => setOwner(e.target.value)} sx={engagementSelectSx(t)}>
                <MenuItem value="all">Everyone</MenuItem>
                {officers.map((o) => (
                  <MenuItem key={o._id} value={o._id}>{o.name}</MenuItem>
                ))}
              </Select>
            </Box>
          </Box>

          <Box sx={{ position: "relative", overflowX: "auto", bgcolor: t.cardBg, border: `1px solid ${t.border}`, borderRadius: "14px" }}>
            {loading && <PageLoader />}
            <Box sx={{ minWidth: 1640, display: "flex" }}>
              {board.columns.map((col) => (
                <Box key={col.key} sx={{ flex: "1 1 0", minWidth: 0, padding: "12px", display: "flex", flexDirection: "column", gap: "10px", borderLeft: `1px solid ${t.borderMuted}` }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "6px", padding: "2px 4px 6px" }}>
                    <Typography sx={{ fontSize: 15, fontWeight: 700 }}>{col.title}</Typography>
                    <Typography sx={{ fontSize: 13, color: t.textMuted }}>{col.totalCount}</Typography>
                  </Box>
                  {col.cards.map((c) => (
                    <Box
                      key={c.id}
                      sx={{ bgcolor: t.cardBg, border: `1px solid ${t.border}`, borderRadius: "10px", padding: "12px", display: "flex", flexDirection: "column", gap: "6px" }}
                    >
                      <Typography component={Link} to={`/dashboard/engagement/clinics/${c.id}`} sx={{ fontSize: 15, fontWeight: 600, color: t.accent, textDecoration: "none" }}>
                        {c.doctorName}
                      </Typography>
                      <Typography sx={{ fontSize: 13, color: t.textSecondary }}>{c.clinicName}{c.city ? `, ${c.city}` : ""}</Typography>
                      <Typography sx={{ fontSize: 13, color: t.textMuted }}>{c.phone}</Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                        {c.term && <EngagementPill t={t} tone="info">{TERM_LABEL[c.term] || c.term}</EngagementPill>}
                        {c.plan && <EngagementPill t={t} tone="mute">{c.plan}</EngagementPill>}
                      </Box>
                      {c.amount && <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{formatAmount(c.amount, c.currency)}</Typography>}
                      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{whenLabel(c)}</Typography>
                      <Typography sx={{ fontSize: 13, color: t.textMuted }}>Prefers {c.pref}</Typography>
                      {c.note && (
                        <EngagementPill t={t} tone={c.noteTone || "warn"} sx={{ whiteSpace: "normal", height: "auto", minHeight: "26px", alignSelf: "flex-start" }}>
                          {c.note}
                        </EngagementPill>
                      )}

                      {(c.notes || []).length > 0 && (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                          {c.notes.map((n, i) => (
                            <Box key={i} sx={{ padding: "6px 8px", bgcolor: t.hoverBg, borderRadius: "6px" }}>
                              <Typography sx={{ fontSize: 12, fontWeight: 600, color: t.textMuted }}>{n.by}, {formatDate(n.createdAt)}</Typography>
                              <Typography sx={{ fontSize: 13, whiteSpace: "pre-wrap" }}>{n.text}</Typography>
                            </Box>
                          ))}
                        </Box>
                      )}
                      {notesOpenId === c.id ? (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", padding: "8px", bgcolor: t.hoverBg, borderRadius: "8px" }}>
                          <TextField
                            size="small"
                            multiline
                            minRows={2}
                            label="New note"
                            value={notesDraft}
                            onChange={(e) => setNotesDraft(e.target.value)}
                            sx={{ bgcolor: t.inputBg }}
                          />
                          <Box sx={{ display: "flex", gap: "8px" }}>
                            <Box
                              component="button"
                              type="button"
                              disabled={savingNotesId === c.id || !notesDraft.trim()}
                              onClick={() => addNote(c.id)}
                              sx={{ ...engagementButtonSx(t, "solid"), opacity: savingNotesId === c.id || !notesDraft.trim() ? 0.6 : 1 }}
                            >
                              {savingNotesId === c.id ? "Adding…" : "Add"}
                            </Box>
                            <Box component="button" type="button" onClick={() => setNotesOpenId("")} sx={{ ...engagementButtonSx(t), border: 0 }}>
                              Close
                            </Box>
                          </Box>
                        </Box>
                      ) : (
                        <Box
                          component="button"
                          type="button"
                          onClick={() => toggleNotes(c)}
                          sx={{ ...engagementButtonSx(t), border: `1px solid ${t.disabledBorder}`, color: t.textPrimary, alignSelf: "flex-start" }}
                        >
                          Add note
                        </Box>
                      )}

                      {(c.canLink || c.canChange || c.canRecord || c.canChurn || c.canPause || c.canResume) && (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                          {c.canLink && c.paymentLinkSent && (
                            <Box
                              component="button"
                              type="button"
                              disabled={sendingId === c.id}
                              onClick={() => sendLink(c.id)}
                              sx={{ ...engagementButtonSx(t), opacity: sendingId === c.id ? 0.6 : 1 }}
                            >
                              {sendingId === c.id ? "Sending…" : "Resend link"}
                            </Box>
                          )}
                          {c.canLink && !c.paymentLinkSent && (
                            <Box component="button" type="button" onClick={() => toggleSendLink(c.id)} sx={{ ...engagementButtonSx(t), border: `1px solid ${t.disabledBorder}`, color: t.textPrimary }}>
                              {sendLinkId === c.id ? "Close" : "Send payment link"}
                            </Box>
                          )}
                          {c.canChange && (
                            <Box component="button" type="button" onClick={() => toggleChanging(c.id)} sx={{ ...engagementButtonSx(t), border: `1px solid ${t.disabledBorder}`, color: t.textPrimary }}>
                              {changingId === c.id ? "Close" : "Change plan"}
                            </Box>
                          )}
                          {c.canRecord && (
                            <Box component="button" type="button" onClick={() => toggleRecording(c.id)} sx={{ ...engagementButtonSx(t), border: `1px solid ${t.disabledBorder}`, color: t.textPrimary }}>
                              {recordingId === c.id ? "Close" : "Record payment"}
                            </Box>
                          )}
                          {c.canChurn && (
                            <Box
                              component="button"
                              type="button"
                              onClick={() => { setChurnPromptId(churnPromptId === c.id ? "" : c.id); setChurnReason(""); }}
                              sx={{ ...engagementButtonSx(t), border: `1px solid ${t.disabledBorder}`, color: t.textPrimary }}
                            >
                              {churnPromptId === c.id ? "Close" : "Mark churned"}
                            </Box>
                          )}
                          {(c.canPause || c.canResume) && (
                            <Box
                              component="button"
                              type="button"
                              disabled={pausingId === c.id}
                              onClick={() => pauseOrResume(c, c.canResume)}
                              sx={{ ...engagementButtonSx(t), border: `1px solid ${t.disabledBorder}`, color: t.textPrimary, opacity: pausingId === c.id ? 0.6 : 1 }}
                            >
                              {pausingId === c.id ? "Saving…" : c.canResume ? "Resume" : "Pause"}
                            </Box>
                          )}
                        </Box>
                      )}
                      {churnPromptId === c.id && (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", padding: "8px", bgcolor: t.hoverBg, borderRadius: "8px" }}>
                          <TextField
                            size="small"
                            label="Reason (e.g. clinic said they're not renewing)"
                            value={churnReason}
                            onChange={(e) => setChurnReason(e.target.value)}
                            sx={{ bgcolor: t.inputBg }}
                          />
                          <Box sx={{ display: "flex", gap: "8px" }}>
                            <Box
                              component="button"
                              type="button"
                              disabled={churningId === c.id}
                              onClick={() => markChurned(c.id, churnReason.trim())}
                              sx={{ ...engagementButtonSx(t, "solid"), opacity: churningId === c.id ? 0.6 : 1 }}
                            >
                              {churningId === c.id ? "Marking…" : "Confirm"}
                            </Box>
                            <Box component="button" type="button" onClick={() => setChurnPromptId("")} sx={{ ...engagementButtonSx(t), border: 0 }}>
                              Cancel
                            </Box>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  ))}
                  {col.cards.length === 0 && <Typography sx={{ fontSize: 13, color: t.textMuted, padding: "4px" }}>Nothing here</Typography>}
                </Box>
              ))}
            </Box>
          </Box>

          {(board.totalPages || 1) > 1 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: "10px" }}>
              <Typography sx={{ fontSize: 13, color: t.textMuted }}>Page {board.page || 1} of {board.totalPages || 1} (per column)</Typography>
              <Box sx={{ display: "flex", gap: "8px" }}>
                <Box
                  component="button"
                  type="button"
                  disabled={boardPage <= 1}
                  onClick={() => setBoardPage((p) => Math.max(1, p - 1))}
                  sx={{ minHeight: 36, padding: "0 14px", borderRadius: "8px", border: `1px solid ${t.disabledBorder}`, bgcolor: t.cardBg, cursor: boardPage <= 1 ? "not-allowed" : "pointer" }}
                >
                  Previous
                </Box>
                <Box
                  component="button"
                  type="button"
                  disabled={boardPage >= (board.totalPages || 1)}
                  onClick={() => setBoardPage((p) => Math.min(board.totalPages || 1, p + 1))}
                  sx={{ minHeight: 36, padding: "0 14px", borderRadius: "8px", border: `1px solid ${t.disabledBorder}`, bgcolor: t.cardBg, cursor: boardPage >= (board.totalPages || 1) ? "not-allowed" : "pointer" }}
                >
                  Next
                </Box>
              </Box>
            </Box>
          )}
        </>
      )}

      {view === "Reconciliation" && (
        <Box sx={{ position: "relative", display: "flex", flexDirection: "column", gap: "10px" }}>
          <Typography sx={{ fontSize: 14, color: t.textSecondary }}>
            Manual payments wait here until Accounts matches them to a bank credit. Verifying extends the subscription;
            rejecting moves the clinic back to grace.
          </Typography>
          <Box sx={{ position: "relative", bgcolor: t.cardBg, border: `1px solid ${t.border}`, borderRadius: "14px", display: "flex", flexDirection: "column" }}>
            {loading && <PageLoader />}
            {reconciliation.length === 0 && <Typography sx={{ fontSize: 14, color: t.textMuted, padding: "18px" }}>Nothing pending verification.</Typography>}
            {reconciliation.map((p, i) => (
              <Box key={p.id} sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "14px", padding: "14px 18px", borderBottom: i === reconciliation.length - 1 ? "none" : `1px solid ${t.borderMuted}` }}>
                <Box sx={{ flex: "1 1 240px", minWidth: 0, display: "flex", flexDirection: "column", gap: "2px" }}>
                  <Typography component={Link} to={`/dashboard/engagement/clinics/${p.doctorId}`} sx={{ fontSize: 15, fontWeight: 600, color: t.accent, textDecoration: "none" }}>{p.doctorName}</Typography>
                  <Typography sx={{ fontSize: 13, color: t.textMuted }}>{p.clinicName}. {TERM_LABEL[p.term] || p.term}{p.period ? `, covers ${p.period}` : ""}</Typography>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", width: 240 }}>
                  <Typography sx={{ fontSize: 15, fontWeight: 700 }}>{formatAmount(p.amount, p.currency)}</Typography>
                  <Typography sx={{ fontSize: 13, color: t.textMuted }}>{p.method}, paid {formatDate(p.paidOn)}</Typography>
                  <Typography sx={{ fontSize: 13, color: t.textMuted }}>Ref {p.reference || "Not given"}. Entered by {p.enteredBy || "—"}</Typography>
                  <Box sx={{ display: "flex", gap: "10px" }}>
                    {p.proofUrl && (
                      <Typography component="a" href={p.proofUrl} target="_blank" rel="noreferrer" sx={{ fontSize: 13, color: t.accent, fontWeight: 600 }}>View proof</Typography>
                    )}
                    {p.invoiceUrl && (
                      <Typography component="a" href={p.invoiceUrl} target="_blank" rel="noreferrer" sx={{ fontSize: 13, color: t.accent, fontWeight: 600 }}>Invoice</Typography>
                    )}
                    {p.receiptUrl && (
                      <Typography component="a" href={p.receiptUrl} target="_blank" rel="noreferrer" sx={{ fontSize: 13, color: t.accent, fontWeight: 600 }}>Receipt</Typography>
                    )}
                  </Box>
                </Box>
                <EngagementPill t={t} tone={p.reference ? "info" : "warn"} sx={{ whiteSpace: "normal", height: "auto", minHeight: "26px", maxWidth: 260 }}>
                  {p.reference ? "Reference given — ready for Accounts to match" : "No reference given — add one to help Accounts match it"}
                </EngagementPill>
                <Box sx={{ display: "flex", gap: "8px", marginLeft: "auto" }}>
                  <Box
                    component="button"
                    type="button"
                    disabled={verifyingId === p.id || rejectingId === p.id}
                    onClick={() => verify(p.id)}
                    sx={{ ...engagementButtonSx(t, "solid"), opacity: verifyingId === p.id || rejectingId === p.id ? 0.6 : 1 }}
                  >
                    {verifyingId === p.id ? "Verifying…" : "Verify"}
                  </Box>
                  <Box
                    component="button"
                    type="button"
                    disabled={verifyingId === p.id || rejectingId === p.id}
                    onClick={() => reject(p.id)}
                    sx={{ ...engagementButtonSx(t), border: `1px solid ${t.disabledBorder}`, color: t.textPrimary, opacity: verifyingId === p.id || rejectingId === p.id ? 0.6 : 1 }}
                  >
                    {rejectingId === p.id ? "Rejecting…" : "Reject"}
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
          {reconciliationTotalPages > 1 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: "10px" }}>
              <Typography sx={{ fontSize: 13, color: t.textMuted }}>Page {reconciliationPage} of {reconciliationTotalPages}</Typography>
              <Box sx={{ display: "flex", gap: "8px" }}>
                <Box
                  component="button"
                  type="button"
                  disabled={reconciliationPage <= 1}
                  onClick={() => setReconciliationPage((p) => Math.max(1, p - 1))}
                  sx={{ minHeight: 36, padding: "0 14px", borderRadius: "8px", border: `1px solid ${t.disabledBorder}`, bgcolor: t.cardBg, cursor: reconciliationPage <= 1 ? "not-allowed" : "pointer" }}
                >
                  Previous
                </Box>
                <Box
                  component="button"
                  type="button"
                  disabled={reconciliationPage >= reconciliationTotalPages}
                  onClick={() => setReconciliationPage((p) => Math.min(reconciliationTotalPages, p + 1))}
                  sx={{ minHeight: 36, padding: "0 14px", borderRadius: "8px", border: `1px solid ${t.disabledBorder}`, bgcolor: t.cardBg, cursor: reconciliationPage >= reconciliationTotalPages ? "not-allowed" : "pointer" }}
                >
                  Next
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      )}

      {view === "Payment history" && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
        <TextField
          size="small"
          placeholder="Search by doctor, clinic or reference"
          value={historySearch}
          onChange={(e) => { setHistoryPage(1); setHistorySearch(e.target.value); }}
          sx={{ width: 320, maxWidth: "100%", bgcolor: t.inputBg }}
        />
        <Box sx={{ position: "relative", overflowX: "auto", bgcolor: t.cardBg, border: `1px solid ${t.border}`, borderRadius: "14px" }}>
          {loading && <PageLoader />}
          <Box component="table" sx={{ width: "100%", minWidth: 1100, borderCollapse: "collapse", fontSize: 14 }}>
            <Box component="thead">
              <Box component="tr" sx={{ textAlign: "left", color: t.textMuted, fontSize: 13 }}>
                {["Paid on", "Doctor and clinic", "Term and period", "Amount", "Method", "Reference", "Proof", "Invoice/Receipt", "Entered by", "Status"].map((h) => (
                  <Box component="th" key={h} sx={{ padding: "12px 16px", borderBottom: `1px solid ${t.border}` }}>{h}</Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {history.length === 0 && (
                <Box component="tr"><Box component="td" colSpan={10} sx={{ padding: "16px", color: t.textMuted }}>No payments on record.</Box></Box>
              )}
              {history.map((p) => (
                <Box component="tr" key={p.id}>
                  <Box component="td" sx={{ padding: "12px 16px", borderBottom: `1px solid ${t.borderMuted}` }}>{formatDate(p.paidOn)}</Box>
                  <Box component="td" sx={{ padding: "12px", borderBottom: `1px solid ${t.borderMuted}` }}>
                    <Typography component={Link} to={`/dashboard/engagement/clinics/${p.doctorId}`} sx={{ fontWeight: 600, color: t.accent, textDecoration: "none" }}>{p.doctorName}</Typography>
                    <Typography sx={{ fontSize: 13, color: t.textMuted }}>{p.clinicName}</Typography>
                  </Box>
                  <Box component="td" sx={{ padding: "12px", borderBottom: `1px solid ${t.borderMuted}` }}>
                    {TERM_LABEL[p.term] || p.term || "—"}
                    <Typography sx={{ fontSize: 13, color: t.textMuted }}>{p.period || "—"}</Typography>
                  </Box>
                  <Box component="td" sx={{ padding: "12px", borderBottom: `1px solid ${t.borderMuted}`, fontWeight: 600 }}>{formatAmount(p.amount, p.currency)}</Box>
                  <Box component="td" sx={{ padding: "12px", borderBottom: `1px solid ${t.borderMuted}` }}><EngagementPill t={t} tone="mute">{p.method}</EngagementPill></Box>
                  <Box component="td" sx={{ padding: "12px", borderBottom: `1px solid ${t.borderMuted}` }}>{p.reference || "—"}</Box>
                  <Box component="td" sx={{ padding: "12px", borderBottom: `1px solid ${t.borderMuted}` }}>
                    {p.proofUrl ? (
                      <Typography component="a" href={p.proofUrl} target="_blank" rel="noreferrer" sx={{ color: t.accent, fontWeight: 600 }}>View</Typography>
                    ) : "—"}
                  </Box>
                  <Box component="td" sx={{ padding: "12px", borderBottom: `1px solid ${t.borderMuted}` }}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                      {p.invoiceUrl && (
                        <Typography component="a" href={p.invoiceUrl} target="_blank" rel="noreferrer" sx={{ color: t.accent, fontWeight: 600 }}>Invoice</Typography>
                      )}
                      {p.receiptUrl && (
                        <Typography component="a" href={p.receiptUrl} target="_blank" rel="noreferrer" sx={{ color: t.accent, fontWeight: 600 }}>Receipt</Typography>
                      )}
                      {!p.invoiceUrl && !p.receiptUrl && (
                        <Box
                          component="label"
                          sx={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: t.accent,
                            cursor: uploadingInvoiceId === p.id ? "not-allowed" : "pointer",
                            opacity: uploadingInvoiceId === p.id ? 0.6 : 1,
                          }}
                        >
                          {uploadingInvoiceId === p.id ? "Uploading…" : "Upload invoice"}
                          <Box
                            component="input"
                            type="file"
                            accept="image/*,application/pdf"
                            disabled={uploadingInvoiceId === p.id}
                            onChange={(e) => {
                              const file = e.target.files?.[0] || null;
                              e.target.value = "";
                              uploadInvoice(p.id, file);
                            }}
                            sx={{ display: "none" }}
                          />
                        </Box>
                      )}
                    </Box>
                  </Box>
                  <Box component="td" sx={{ padding: "12px", borderBottom: `1px solid ${t.borderMuted}` }}>{p.enteredBy || "—"}</Box>
                  <Box component="td" sx={{ padding: "12px", borderBottom: `1px solid ${t.borderMuted}` }}>
                    <EngagementPill t={t} tone={p.status === "paid" ? "ok" : p.status === "initiated" || p.status === "partially_paid" ? "warn" : p.status === "failed" ? "crit" : "mute"}>{p.status}</EngagementPill>
                    {p.isInstallment && p.installments?.length > 0 && (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px", mt: "6px" }}>
                        {p.installments.map((inst) => (
                          <EngagementPill
                            key={inst.installmentNo}
                            t={t}
                            tone={inst.status === "paid" ? "ok" : "warn"}
                            sx={{ fontSize: 11 }}
                          >
                            #{inst.installmentNo}: {formatAmount(inst.amount, p.currency)}{inst.status === "paid" && inst.paidAt ? ` (${formatDate(inst.paidAt)})` : ""}
                          </EngagementPill>
                        ))}
                      </Box>
                    )}
                    {p.isInstallment && p.status === "partially_paid" && (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", mt: "6px" }}>
                        <Typography sx={{ fontSize: 12, color: t.textMuted }}>{p.installmentsPaid} of {p.installmentsTotal} paid</Typography>
                        <Box
                          component="button"
                          type="button"
                          disabled={collectingId === p.id}
                          onClick={() => collectInstallment(p.id)}
                          sx={{ ...engagementButtonSx(t), minHeight: 32, padding: "0 10px", fontSize: 12, opacity: collectingId === p.id ? 0.6 : 1 }}
                        >
                          {collectingId === p.id ? "Collecting…" : `Collect next (${formatAmount(p.nextInstallmentAmount, p.currency)})`}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
        {historyTotalPages > 1 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: "10px" }}>
            <Typography sx={{ fontSize: 13, color: t.textMuted }}>Page {historyPage} of {historyTotalPages}</Typography>
            <Box sx={{ display: "flex", gap: "8px" }}>
              <Box
                component="button"
                type="button"
                disabled={historyPage <= 1}
                onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                sx={{ minHeight: 36, padding: "0 14px", borderRadius: "8px", border: `1px solid ${t.disabledBorder}`, bgcolor: t.cardBg, cursor: historyPage <= 1 ? "not-allowed" : "pointer" }}
              >
                Previous
              </Box>
              <Box
                component="button"
                type="button"
                disabled={historyPage >= historyTotalPages}
                onClick={() => setHistoryPage((p) => Math.min(historyTotalPages, p + 1))}
                sx={{ minHeight: 36, padding: "0 14px", borderRadius: "8px", border: `1px solid ${t.disabledBorder}`, bgcolor: t.cardBg, cursor: historyPage >= historyTotalPages ? "not-allowed" : "pointer" }}
              >
                Next
              </Box>
            </Box>
          </Box>
        )}
        </Box>
      )}

      <Typography sx={{ fontSize: 13, color: t.textMuted }}>
        Stages come from real subscription end dates. "Renewed" means a new subscription for that doctor started this
        month; "Churned" means past grace with no newer subscription, or a manual override here. Payment links are real
        Razorpay links. Recording a cash/manual payment never changes the subscription itself — it only queues it for
        Accounts to verify and apply. Upgrades can apply now, prorated, or at the next renewal; downgrades always
        apply at the next renewal.
      </Typography>
    </Box>
  );
};

export default EngagementRenewals;
